import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/ui/password-input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
    Shield, ShieldOff, KeyRound, Mail, BadgeCheck, Lock,
    AlertTriangle, RefreshCw, Copy,
} from "lucide-react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const ROLE_LABEL = {
    admin: "Administrator",
    sme: "Subject-Matter Expert",
    instructor: "Instructor",
    staff: "Staff",
};

/**
 * Staff-facing profile page — used by admins, SMEs, and instructors.
 *
 * Shows identity, role badge, and (for admins) a security section with
 * a real 2FA enable/disable toggle plus a "Change password" link. The
 * 2FA disable flow is gated behind both the current password and a
 * fresh authenticator code (backup codes also accepted) — matching the
 * canonical pattern from the auth playbook.
 */
export default function StaffProfile() {
    const { user, refresh, loading } = useAuth();
    const [twoFA, setTwoFA] = useState(null);
    const [disableOpen, setDisableOpen] = useState(false);

    const loadTwoFA = async () => {
        if (user?.role !== "admin") return;
        try {
            const { data } = await api.get("/auth/2fa/status");
            setTwoFA(data);
        } catch {
            setTwoFA({ enabled: false, backup_codes_remaining: 0 });
        }
    };

    useEffect(() => {
        loadTwoFA();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.role]);

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-48 w-full" />
            </div>
        );
    }
    if (!user) return null;

    const initials = (user.name || "")
        .split(" ").filter(Boolean).slice(0, 2)
        .map((p) => p[0]?.toUpperCase()).join("") || "A";
    const isAdmin = user.role === "admin";

    return (
        <div
            className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6"
            data-testid="staff-profile-page"
        >
            {/* Identity header */}
            <header className="flex items-center gap-4">
                <div
                    className="h-16 w-16 rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center font-display text-2xl"
                    data-testid="profile-avatar"
                >
                    {initials}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="font-display text-3xl text-foreground tracking-tight truncate">
                            {user.name}
                        </h1>
                        <Badge
                            variant="outline"
                            className="bg-primary/10 text-primary border-primary/30 capitalize"
                            data-testid="profile-role-badge"
                        >
                            <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                            {ROLE_LABEL[user.role] || user.role}
                        </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                        <Mail className="h-3.5 w-3.5" />
                        {user.email}
                    </div>
                </div>
            </header>

            {/* Account info */}
            <Card>
                <CardContent className="p-6 space-y-4">
                    <h2 className="font-display text-xl text-foreground">Account</h2>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <Row label="Display name" value={user.name || "—"} />
                        <Row label="Email" value={user.email} />
                        <Row label="Role" value={ROLE_LABEL[user.role] || user.role} />
                        <Row label="Member since" value={fmtDate(user.created_at)} />
                    </dl>
                </CardContent>
            </Card>

            {/* Security section */}
            <Card data-testid="security-section">
                <CardContent className="p-6 space-y-5">
                    <h2 className="font-display text-xl text-foreground flex items-center gap-2">
                        <Lock className="h-5 w-5 text-primary" /> Security
                    </h2>

                    {/* Password */}
                    <div className="flex items-start justify-between gap-4 pb-5 border-b border-border">
                        <div>
                            <div className="font-medium text-foreground">Password</div>
                            <div className="text-sm text-muted-foreground">
                                Used to sign in to your account.
                            </div>
                        </div>
                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            data-testid="change-password-link"
                        >
                            <Link to="/account/change-password">
                                <KeyRound className="h-4 w-4 mr-2" />
                                Change password
                            </Link>
                        </Button>
                    </div>

                    {/* 2FA — admin only */}
                    {isAdmin ? (
                        <TwoFactorRow
                            twoFA={twoFA}
                            onDisableClick={() => setDisableOpen(true)}
                            onSetupClick={() => (window.location.href = "/account/2fa/setup")}
                            onRefresh={loadTwoFA}
                        />
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            Two-factor authentication is currently only available for
                            administrator accounts.
                        </div>
                    )}
                </CardContent>
            </Card>

            {isAdmin && (
                <DisableTwoFADialog
                    open={disableOpen}
                    onOpenChange={setDisableOpen}
                    onSuccess={async () => {
                        await loadTwoFA();
                        await refresh?.();
                    }}
                />
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                {label}
            </dt>
            <dd className="text-foreground mt-0.5">{value}</dd>
        </div>
    );
}

function TwoFactorRow({ twoFA, onDisableClick, onSetupClick, onRefresh }) {
    const enabled = !!twoFA?.enabled;
    return (
        <div
            className="flex items-start justify-between gap-4"
            data-testid="twofa-row"
        >
            <div className="min-w-0">
                <div className="font-medium text-foreground flex items-center gap-2">
                    Two-factor authentication
                    {enabled ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
                            <Shield className="h-3 w-3 mr-1" /> On
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                            <ShieldOff className="h-3 w-3 mr-1" /> Off
                        </Badge>
                    )}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                    {enabled
                        ? `Enrolled ${fmtDate(twoFA.enrolled_at)}. ${twoFA.backup_codes_remaining || 0} backup codes remaining.`
                        : "Add a 6-digit code from your authenticator app on every sign-in."}
                </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {enabled ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onDisableClick}
                        data-testid="twofa-disable-button"
                        className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                    >
                        <ShieldOff className="h-4 w-4 mr-2" />
                        Turn off
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        onClick={onSetupClick}
                        data-testid="twofa-enable-button"
                    >
                        <Shield className="h-4 w-4 mr-2" />
                        Turn on
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRefresh}
                    className="text-xs text-muted-foreground"
                    aria-label="Refresh 2FA status"
                >
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                </Button>
            </div>
        </div>
    );
}

function DisableTwoFADialog({ open, onOpenChange, onSuccess }) {
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setPassword("");
        setCode("");
        setSubmitting(false);
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (submitting) return;
        if (!password || !code.trim()) {
            toast.error("Both your password and a fresh code are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/2fa/disable", {
                current_password: password,
                code: code.trim(),
            });
            toast.success("Two-factor authentication is now off for your account.");
            reset();
            onOpenChange(false);
            await onSuccess?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't disable 2FA.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) reset();
                onOpenChange(o);
            }}
        >
            <DialogContent
                className="sm:max-w-md"
                data-testid="disable-twofa-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-destructive">
                        <AlertTriangle className="h-5 w-5" />
                        Turn off two-factor authentication?
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        Your account will only be protected by your password after this.
                        We recommend keeping 2FA on for administrator accounts.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="current-password">Your current password</Label>
                        <PasswordInput
                            id="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoFocus
                            data-testid="twofa-disable-password"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="twofa-code">
                            6-digit code from your authenticator app
                            <span className="text-xs text-muted-foreground ml-1 font-normal">
                                — or one of your backup codes
                            </span>
                        </Label>
                        <Input
                            id="twofa-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="123456 or BACKUP-XXXXX"
                            inputMode="text"
                            autoComplete="one-time-code"
                            required
                            data-testid="twofa-disable-code"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                                reset();
                                onOpenChange(false);
                            }}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="destructive"
                            disabled={submitting}
                            data-testid="twofa-disable-confirm"
                        >
                            {submitting ? "Turning off…" : "Turn off 2FA"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
